// Domain enum → label maps, SOURCE language. Each is a complete Record, so a backend value
// with no label does not compile. FIGC categories keep Italian VALUES: they are data.

import type {
  ActionType,
  AppointmentRole,
  KitDesign,
  KitRank,
  MatchKitChoice,
  PlayerFoot,
  PlayerPosition,
  Sex,
  SubscriptionStatus,
  TeamGender,
  TeamSheetStaffRead,
} from '@volee/api-client'

export type {
  KitDesign,
  KitRank,
  MatchKitChoice,
  PlayerFoot,
  PlayerPosition,
  Sex,
  SubscriptionStatus,
  TeamGender,
}

/* From the contract since the highlights carry it (rule 5): a type added on the backend without a
 * label is a compile error. */
export type { ActionType }

export const EN_ACTION_TYPES: Record<ActionType, string> = {
  goal: 'Goal',
  own_goal: 'Own goal',
  assist: 'Assist',
  shot: 'Shot',
  save: 'Save',
  dribble: 'Dribble',
  tackle: 'Tackle',
  key_pass: 'Key pass',
  recovery: 'Recovery',
  yellow_card: 'Yellow card',
  red_card: 'Red card',
  substitution: 'Substitution',
}

/* The three roles a club appoints. The type comes from the contract and is no longer restated
 * here (rule 5), so one added on the backend without a label is a compile error. */
export type { AppointmentRole }

export const EN_APPOINTMENT_ROLES: Record<AppointmentRole, string> = {
  official: 'Club official',
  team_official: 'Team official',
  coach: 'Coach',
}

/** Three forms of one entry, not three catalogues: a row names one player, a filter chip
 *  names everyone who plays there, and `short` fits the match page's compact squad card —
 *  same device as `PROVINCES`, which carries two attributes. */
export interface PositionLabel {
  readonly one: string
  readonly many: string
  readonly short: string
}

export const EN_PLAYER_POSITIONS: Record<PlayerPosition, PositionLabel> = {
  goalkeeper: { one: 'Goalkeeper', many: 'Goalkeepers', short: 'GK' },
  defender: { one: 'Defender', many: 'Defenders', short: 'DEF' },
  midfielder: { one: 'Midfielder', many: 'Midfielders', short: 'MID' },
  forward: { one: 'Forward', many: 'Forwards', short: 'FWD' },
}

export const EN_PLAYER_FEET: Record<PlayerFoot, string> = {
  left: 'Left',
  right: 'Right',
  both: 'Both',
}

/* A person's sex, which is not the sentence a team's gender is: separate labels over the
 * same two values. */
export const EN_SEXES: Record<Sex, string> = {
  male: 'Male',
  female: 'Female',
}

export type MatchStatus = 'scheduled' | 'played' | 'postponed'

export const EN_MATCH_STATUSES: Record<MatchStatus, string> = {
  scheduled: 'Scheduled',
  played: 'Played',
  postponed: 'Postponed',
}

// Stripe's statuses, verbatim (spec §4.2). Only the covering ones reach a chip.
export const EN_SUBSCRIPTION_STATUSES: Record<SubscriptionStatus, string> = {
  incomplete: 'Awaiting payment',
  incomplete_expired: 'Payment not completed',
  trialing: 'Subscribed',
  active: 'Subscribed',
  past_due: 'Needs updating',
  canceled: 'Ended',
  unpaid: 'Unpaid',
  paused: 'Paused',
}

/* FIGC categories: the values are Italian because they are the federation's names. */
export type AgeGroup =
  'seniores' | 'juniores' | 'allievi' | 'giovanissimi' | 'esordienti' | 'pulcini'

export const EN_AGE_GROUPS: Record<AgeGroup, string> = {
  seniores: 'Seniores',
  juniores: 'Juniores',
  allievi: 'Allievi',
  giovanissimi: 'Giovanissimi',
  esordienti: 'Esordienti',
  pulcini: 'Pulcini',
}

export type GuardianRelationship = 'mother' | 'father' | 'guardian'

/** The championship a side plays, said on every squad and not only on the women's: an
 *  unmarked row would mean both «male» and «we do not know». */
export const EN_TEAM_GENDERS: Record<TeamGender, string> = {
  male: 'Male',
  female: 'Female',
  open: 'Mixed',
}

/** A word that agrees with a count — one, or more than one. */
export interface CountedNoun {
  readonly one: string
  readonly many: string
}

/** Who is in a squad's rosa, in the squad's own gender: Italian says «giocatrici» of a women's
 *  side, so the word follows the championship the squad plays. */
export const EN_SQUAD_PLAYER_NOUNS: Record<TeamGender, CountedNoun> = {
  male: { one: 'player', many: 'players' },
  female: { one: 'player', many: 'players' },
  open: { one: 'player', many: 'players' },
}

/** A squad's coaches, counted — one word whatever the squad's gender. */
export const EN_SQUAD_COACH_NOUN: CountedNoun = { one: 'coach', many: 'coaches' }

export const EN_GUARDIAN_RELATIONSHIPS: Record<GuardianRelationship, string> = {
  mother: 'Mother',
  father: 'Father',
  guardian: 'Guardian',
}

/** An order of preference, never home and away: the glossary's «prima» and «seconda». */
export const EN_KIT_RANKS: Record<KitRank, string> = {
  first: 'First kit',
  second: 'Second kit',
}

/** La divisa di gara: one of the two kits, by the rank's own name, or colours for one match. */
export const EN_MATCH_KIT_CHOICES: Record<MatchKitChoice, string> = {
  ...EN_KIT_RANKS,
  team_first: 'First kit of the squad',
  team_second: 'Second kit of the squad',
  other: 'Other colours',
}

/** The club's kits when a squad's stand beside them, or in a summary: they say whose they are. */
export const EN_CLUB_KIT_NAMES: Record<KitRank, string> = {
  first: 'First kit of the club',
  second: 'Second kit of the club',
}

/** Who of the staff goes with the squad, as the distinta lists them under the players. */
export type TeamSheetStaffRole = TeamSheetStaffRead['role']

export const EN_TEAM_SHEET_STAFF_ROLES: Record<TeamSheetStaffRole, string> = {
  coach: 'Coach',
  accompanying_official: 'Accompanying official',
  referee_liaison_official: 'Referee liaison',
  doctor: 'Doctor',
  masseur: 'Masseur',
  assistant_referee: 'Assistant referee',
}

export const EN_KIT_DESIGNS: Record<KitDesign, string> = {
  solid: 'Solid',
  vertical_stripes: 'Vertical stripes',
  horizontal_stripes: 'Horizontal stripes',
  diagonal_stripes: 'Diagonal stripes',
  halves: 'Halves',
}

/** Shown in place of a value no catalogue knows. */
export const EN_UNKNOWN_VALUE = 'Not specified'
